/**
 * Cart-page interactions (Phase 3.16, partial-refresh in 3.17c-i).
 *
 * The view (`Views/Checkout/Cart.cshtml`) emits data attributes for every
 * mutation hook; this module attaches click + change handlers and POSTs to
 * the SurfaceController endpoints. On success the page is partially
 * refreshed: we re-fetch the same `/checkout` URL and swap the cart
 * container's inner HTML in place. Scroll position + payment-form state
 * (3.18) are preserved.
 *
 * Markup contract:
 *   [data-amri-cart-line][data-line-id]                — wrapper for one line
 *   [data-amri-line-remove]                            — click to remove line
 *   [data-amri-line-update][data-direction="-1|1"]     — click to increment/decrement
 *   [data-amri-line-count-input][data-line-id]         — manual qty input (blur to commit)
 *   [data-amri-discount-input]                         — discount-code textbox
 *   [data-amri-apply-discount]                         — click to apply discount
 *   [data-amri-discount-message]                       — error/status placeholder
 *   [data-amri-mail-hard-copy]                         — checkbox; change auto-posts
 *   [data-amri-bundle-add] / [data-amri-bundle-remove] — upsell row forms (Phase 3.17c-i)
 */

import { refreshCartCount } from '../chrome/cart-counter';

const ENDPOINTS = {
    removeLine: '/umbraco/surface/checkoutsurface/removecartline',
    updateLineCount: '/umbraco/surface/checkoutsurface/updatelinecount',
    updateGroupOrderLine: '/umbraco/surface/checkoutsurface/updategrouporderline',
    applyDiscount: '/umbraco/surface/checkoutsurface/applydiscountcode',
    saveMailHardCopy: '/umbraco/surface/checkoutsurface/savemailhardcopy',
    updateShippingMethod: '/umbraco/surface/checkoutsurface/updateshippingmethod',
    addToCart: '/umbraco/surface/checkoutsurface/addtocart',
    removeProductFromCart: '/umbraco/surface/checkoutsurface/removeproductfromcart',
    addSuggestedMaterialToCart: '/umbraco/surface/checkoutsurface/addsuggestedmaterialtocart',
} as const;

const CART_PAGE = '/checkout';
const CART_CONTAINER = '.amri-checkout--container';

interface BasicResponse {
    success: boolean;
}

/**
 * R5-1 — read the antiforgery token rendered by `@Html.AntiForgeryToken()` in
 * Cart.cshtml. `AddSuggestedMaterialToCart` validates it; the other checkout
 * POSTs ignore the extra field harmlessly. Returns null when absent (e.g. an
 * unexpected DOM state) so the caller never sends an empty-named field that
 * would break model binding.
 */
function readAntiforgeryToken(): string | null {
    const input = document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');
    const value = input?.value ?? '';
    return value === '' ? null : value;
}

async function postForm(url: string, body: Record<string, string>): Promise<BasicResponse | null> {
    // DRY — every checkout POST carries the antiforgery token so the ones that
    // validate it (AddSuggestedMaterialToCart) succeed; the rest ignore it.
    const token = readAntiforgeryToken();
    const payload = token === null ? body : { ...body, __RequestVerificationToken: token };
    const params = new URLSearchParams(payload);
    try {
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Accept: 'application/json',
            },
            body: params.toString(),
        });
        if (!response.ok) {
            return null;
        }
        return (await response.json()) as BasicResponse;
    } catch {
        return null;
    }
}

/**
 * Phase 3.17c-i partial-refresh — re-fetch /checkout, swap the cart container
 * in-place, and refresh the cart counter. Preserves scroll position and any
 * non-cart-container state on the page. Falls back to a full reload on error
 * so the cart never gets stuck in a stale state.
 */
async function refreshCartPage(): Promise<void> {
    // QA feedback (86bb1fdza) — the partial-refresh swapped the cart silently,
    // giving no indication anything was happening ("confusing when no loading
    // icon is displayed", most noticeable adding a suggested material on mobile).
    // Show a lightweight loading overlay for the duration of the fetch + swap so
    // every cart mutation gives visible feedback. The container element survives
    // the innerHTML swap, so the class set here is still present to clear in the
    // `finally`.
    const refreshingContainer = document.querySelector<HTMLElement>(CART_CONTAINER);
    refreshingContainer?.classList.add('is-refreshing');
    refreshingContainer?.setAttribute('aria-busy', 'true');
    try {
        const response = await fetch(CART_PAGE, {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'text/html' },
        });
        if (!response.ok) {
            window.location.reload();
            return;
        }
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const nextContainer = doc.querySelector(CART_CONTAINER);
        const currentContainer = document.querySelector(CART_CONTAINER);
        if (nextContainer === null || currentContainer === null) {
            window.location.reload();
            return;
        }
        // Capture which mobile-toggler sections are currently expanded BEFORE the
        // swap. The `.open` class is a runtime toggle (see `onMobileToggler`) that
        // the freshly server-rendered markup never carries, so a naive innerHTML
        // replace re-renders the "Add suggested materials" section collapsed and
        // forces the user to re-expand it after every add (QA #12 / 86bb22p59 —
        // legacy keeps it open because it updates the DOM in place). Snapshot the
        // open togglers by selector, then re-apply after the swap.
        const openTogglerSelectors = Array.from(
            currentContainer.querySelectorAll<HTMLElement>('[data-mobile-toggler]'))
            .map(toggler => toggler.getAttribute('data-mobile-toggler'))
            .filter((selector): selector is string => selector !== null && selector !== '')
            .filter(selector => document.querySelector<HTMLElement>(selector)?.classList.contains('open') === true);

        currentContainer.innerHTML = nextContainer.innerHTML;

        for (const selector of openTogglerSelectors) {
            const content = document.querySelector<HTMLElement>(selector);
            content?.classList.add('open');
            document.querySelectorAll<HTMLElement>(`[data-mobile-toggler="${selector}"]`)
                .forEach(toggler => toggler.classList.add('active'));
        }
        void refreshCartCount();
    } catch {
        window.location.reload();
    } finally {
        refreshingContainer?.classList.remove('is-refreshing');
        refreshingContainer?.removeAttribute('aria-busy');
    }
}

function findLineId(target: HTMLElement): string | null {
    const wrapper = target.closest<HTMLElement>('[data-amri-cart-line]');
    return wrapper?.getAttribute('data-line-id') ?? null;
}

function setDiscountMessage(text: string | null, isError: boolean): void {
    const node = document.querySelector<HTMLElement>('[data-amri-discount-message]');
    const input = document.querySelector<HTMLInputElement>('[data-amri-discount-input]');
    if (!node) {
        return;
    }
    // The message element carries `.amri-field-error` (canonical inline-error
    // style) and hides while empty via `:empty` CSS, so clearing the text is all
    // that's needed to hide it. `has-error` on the input gives the red border,
    // matching the payment/account fields.
    if (text === null || text === '') {
        node.textContent = '';
        input?.classList.remove('has-error');
        return;
    }
    node.textContent = text;
    input?.classList.toggle('has-error', isError);
}

async function onLineRemove(button: HTMLElement): Promise<void> {
    const lineId = findLineId(button);
    if (lineId === null) {
        return;
    }
    const result = await postForm(ENDPOINTS.removeLine, { cartLineId: lineId });
    if (result?.success === true) {
        await refreshCartPage();
    }
}

/**
 * Shared +/- step behavior used by every `_QuantityStepper` partial render.
 * Pure-DOM: finds the input, clamps + bumps the value, and dispatches a
 * `change` event so context-specific listeners can commit the new value
 * to the cart-line or suggested-material upsell.
 */
function onQuantityStep(button: HTMLElement): void {
    const direction = button.getAttribute('data-amri-qty-step');
    if (direction !== '-' && direction !== '+') {
        return;
    }
    const stepper = button.closest<HTMLElement>('.amri-checkout--item-quantity');
    const input = stepper?.querySelector<HTMLInputElement>('[data-amri-qty-input]') ?? null;
    if (input === null) {
        return;
    }
    // Read min/max from the input's attributes. Cannot use `|| <default>`
    // here — Number.parseInt("0", 10) === 0 which is falsy, so the group
    // cart's `min="0"` would silently default back to 1 and cause the first
    // `+` click to jump 0 → 2. Use Number.isFinite to keep 0 a valid bound.
    const minParsed = Number.parseInt(input.getAttribute('min') ?? '1', 10);
    const maxParsed = Number.parseInt(input.getAttribute('max') ?? '99', 10);
    const min = Number.isFinite(minParsed) ? minParsed : 1;
    const max = Number.isFinite(maxParsed) ? maxParsed : 99;
    const current = Number.parseInt(input.value, 10);
    const base = Number.isFinite(current) && current >= min ? current : min;
    const next = direction === '+' ? Math.min(max, base + 1) : Math.max(min, base - 1);
    if (next === base) {
        return;
    }
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Phase 3.21d.2b — per-click qty update for one row of the group cart.
 * Fires on every +/- click. POSTs to the single-row surface endpoint so the
 * server can recompute the cart-level totals (subtotal + GroupDiscount tier
 * + total), then `refreshCartPage()` swaps the cart container in-place so
 * the ORDER SUMMARY rerenders with the new values.
 */
async function onGroupOrderLineQuantityChange(row: HTMLElement, count: number): Promise<void> {
    const productType = row.getAttribute('data-product-type');
    if (productType === null || productType === '') {
        return;
    }
    const result = await postForm(ENDPOINTS.updateGroupOrderLine, {
        productType,
        count: String(count),
    });
    if (result?.success === true) {
        await refreshCartPage();
    }
}

async function onCartLineQuantityChange(input: HTMLInputElement): Promise<void> {
    const lineWrapper = input.closest<HTMLElement>('[data-amri-cart-line]');
    if (lineWrapper === null) {
        return;
    }
    const lineId = lineWrapper.getAttribute('data-line-id');
    if (lineId === null || lineId === '') {
        return;
    }
    const raw = Number.parseInt(input.value, 10);
    if (!Number.isFinite(raw) || raw < 1 || raw > 99) {
        return;
    }
    const result = await postForm(ENDPOINTS.updateLineCount, { cartLineId: lineId, count: String(raw) });
    if (result?.success === true) {
        await refreshCartPage();
    }
}

async function onApplyDiscount(): Promise<void> {
    const input = document.querySelector<HTMLInputElement>('[data-amri-discount-input]');
    const code = input?.value?.trim() ?? '';
    if (code === '') {
        setDiscountMessage('Enter a discount code.', true);
        return;
    }
    setDiscountMessage(null, false);
    const result = await postForm(ENDPOINTS.applyDiscount, { code });
    if (result?.success === true) {
        await refreshCartPage();
    } else {
        setDiscountMessage('That discount code is not valid.', true);
    }
}

/**
 * Step-1 shipping-method dropdown (Standard / Express) → persist the choice
 * then partial-refresh so the Order Summary total re-renders with the new
 * shipping cost. Reverts the visual selection on failure (e.g. the books-guard
 * rejected the change) so the control never drifts from the persisted cart.
 */
async function onShippingMethodChange(select: HTMLSelectElement): Promise<void> {
    const previous = select.getAttribute('data-amri-selected') ?? select.value;
    const result = await postForm(ENDPOINTS.updateShippingMethod, { shipmentTypeId: select.value });
    if (result?.success === true) {
        await refreshCartPage();
    } else {
        select.value = previous;
    }
}

async function onMailHardCopyChange(checkbox: HTMLInputElement): Promise<void> {
    const result = await postForm(ENDPOINTS.saveMailHardCopy, { mailHardCopy: checkbox.checked ? 'true' : 'false' });
    if (result?.success !== true) {
        // Revert the visual state if the save failed.
        checkbox.checked = !checkbox.checked;
    }
}

/**
 * Phase 3.17c-i upsell row click → POST add/remove with productType, then
 * partial-refresh. The plain HTML form fallback still works without JS
 * (the controller content-negotiates and 302s back to /checkout).
 */
async function onBundleToggleSubmit(form: HTMLFormElement): Promise<void> {
    const productTypeInput = form.querySelector<HTMLInputElement>('input[name="productType"]');
    const productType = productTypeInput?.value;
    if (productType === undefined || productType === '') {
        return;
    }
    const url = form.dataset.amriBundleRemove !== undefined
        ? ENDPOINTS.removeProductFromCart
        : ENDPOINTS.addToCart;
    const result = await postForm(url, { productType });
    if (result?.success === true) {
        await refreshCartPage();
    }
}

function readSuggestedRowState(row: HTMLElement): { productId: string; count: number; inCart: boolean; cartLineId: string | null } | null {
    const productId = row.getAttribute('data-amri-suggested-row');
    if (productId === null || productId === '') {
        return null;
    }
    const inCart = row.getAttribute('data-amri-suggested-in-cart') === 'true';
    const cartLineId = row.getAttribute('data-amri-suggested-line-id');
    const input = row.querySelector<HTMLInputElement>('[data-amri-qty-input]');
    const rawCount = input?.value ?? '1';
    const parsed = Number.parseInt(rawCount, 10);
    const count = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    return { productId, count, inCart, cartLineId };
}

async function addSuggestedMaterial(productId: string, count: number): Promise<void> {
    const result = await postForm(ENDPOINTS.addSuggestedMaterialToCart, { productId, count: String(count) });
    if (result?.success === true) {
        await refreshCartPage();
    }
}

async function removeSuggestedMaterial(cartLineId: string): Promise<void> {
    const result = await postForm(ENDPOINTS.removeLine, { cartLineId });
    if (result?.success === true) {
        await refreshCartPage();
    }
}

async function updateSuggestedMaterialCount(cartLineId: string, count: number): Promise<void> {
    const result = await postForm(ENDPOINTS.updateLineCount, { cartLineId, count: String(count) });
    if (result?.success === true) {
        await refreshCartPage();
    }
}

async function onSuggestedMaterialQuantityChange(input: HTMLInputElement): Promise<void> {
    const row = input.closest<HTMLElement>('[data-amri-suggested-row]');
    if (row === null) {
        return;
    }
    const state = readSuggestedRowState(row);
    if (state === null || !state.inCart || state.cartLineId === null || state.cartLineId === '') {
        return;
    }
    const raw = Number.parseInt(input.value, 10);
    if (!Number.isFinite(raw) || raw < 1) {
        return;
    }
    await updateSuggestedMaterialCount(state.cartLineId, raw);
}

async function onSuggestedMaterialCheckboxChange(checkbox: HTMLInputElement): Promise<void> {
    const row = checkbox.closest<HTMLElement>('[data-amri-suggested-row]');
    if (row === null) {
        return;
    }
    const state = readSuggestedRowState(row);
    if (state === null) {
        return;
    }

    if (checkbox.checked) {
        if (state.inCart) {
            return;
        }
        await addSuggestedMaterial(state.productId, state.count);
        return;
    }

    if (state.inCart && state.cartLineId !== null && state.cartLineId !== '') {
        await removeSuggestedMaterial(state.cartLineId);
    }
}

function onSuggestedMaterialToggle(titleEl: HTMLElement): void {
    const togglerId = titleEl.getAttribute('data-amri-suggested-toggle');
    if (togglerId === null || togglerId === '') {
        return;
    }
    const content = document.querySelector<HTMLElement>(`[data-amri-suggested-toggle-target="${togglerId}"]`);
    if (content === null) {
        return;
    }
    content.hidden = !content.hidden;
    // Flip EVERY chevron bound to this toggler id, not just the one clicked:
    // each suggested row has a desktop chevron (inside `.suggested-item-title`)
    // AND a mobile chevron (a direct-child `<i>` shown under 768px). Only one is
    // visible at a time, but keeping both in sync means the affordance is correct
    // whichever breakpoint the user is at (QA #5 restored the mobile chevron).
    const icon = content.hidden ? 'keyboard_arrow_down' : 'keyboard_arrow_up';
    document.querySelectorAll<HTMLElement>(`[data-amri-suggested-toggle="${togglerId}"]`).forEach(el => {
        const chevron = el.matches('i.material-icons') ? el : el.querySelector<HTMLElement>('i.material-icons');
        if (chevron !== null) {
            chevron.textContent = icon;
        }
    });
}

function onMobileToggler(toggleEl: HTMLElement): void {
    const selector = toggleEl.getAttribute('data-mobile-toggler');
    if (selector === null || selector === '') {
        return;
    }
    const content = document.querySelector<HTMLElement>(selector);
    if (content === null) {
        return;
    }
    const isOpen = content.classList.toggle('open');
    toggleEl.classList.toggle('active', isOpen);
}

let initialised = false;

export function initCartPage(): void {
    if (initialised) {
        return;
    }
    initialised = true;

    document.addEventListener('click', event => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }
        const removeBtn = target.closest<HTMLElement>('[data-amri-line-remove]');
        if (removeBtn !== null) {
            event.preventDefault();
            void onLineRemove(removeBtn);
            return;
        }
        const qtyStepBtn = target.closest<HTMLElement>('[data-amri-qty-step]');
        if (qtyStepBtn !== null) {
            event.preventDefault();
            onQuantityStep(qtyStepBtn);
            return;
        }
        const applyBtn = target.closest<HTMLElement>('[data-amri-apply-discount]');
        if (applyBtn !== null) {
            event.preventDefault();
            void onApplyDiscount();
            return;
        }
        const suggestedTitle = target.closest<HTMLElement>('[data-amri-suggested-toggle]');
        if (suggestedTitle !== null) {
            event.preventDefault();
            onSuggestedMaterialToggle(suggestedTitle);
            return;
        }
        const mobileToggler = target.closest<HTMLElement>('[data-mobile-toggler]');
        if (mobileToggler !== null) {
            onMobileToggler(mobileToggler);
        }
    });

    document.addEventListener('change', event => {
        const target = event.target;
        if (target instanceof HTMLSelectElement && target.matches('[data-amri-shipping-method]')) {
            void onShippingMethodChange(target);
            return;
        }
        if (!(target instanceof HTMLInputElement)) {
            return;
        }
        if (target.matches('[data-amri-mail-hard-copy]')) {
            void onMailHardCopyChange(target);
            return;
        }
        if (target.type === 'checkbox' && target.closest('[data-amri-suggested-trigger]') !== null) {
            void onSuggestedMaterialCheckboxChange(target);
            return;
        }
        if (target.matches('[data-amri-qty-input]')) {
            if (target.closest('[data-amri-cart-line]') !== null) {
                void onCartLineQuantityChange(target);
                return;
            }
            if (target.closest('[data-amri-suggested-row]') !== null) {
                void onSuggestedMaterialQuantityChange(target);
                return;
            }
            // Phase 3.21d.2b — group-order row: mirror visible qty into the
            // sibling hidden field (so "Proceed with order" still works
            // without JS), THEN POST the new qty so the Order Summary
            // recomputes (Sub-Total + Group Discount tier callout). The
            // follow-up refreshCartPage() swaps the cart container.
            const groupRow = target.closest<HTMLElement>('[data-amri-group-order-row]');
            if (groupRow !== null) {
                const hidden = groupRow.querySelector<HTMLInputElement>('[data-amri-group-order-qty]');
                const parsed = Number.parseInt(target.value, 10);
                const safe = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
                if (hidden !== null) {
                    hidden.value = String(safe);
                }
                void onGroupOrderLineQuantityChange(groupRow, safe);
            }
        }
    });

    document.addEventListener('submit', event => {
        const target = event.target;
        if (!(target instanceof HTMLFormElement)) {
            return;
        }
        if (target.matches('[data-amri-bundle-add], [data-amri-bundle-remove]')) {
            event.preventDefault();
            void onBundleToggleSubmit(target);
        }
    });

    // Enter inside a quantity field must NOT submit the enclosing form. The
    // group-order rows wrap their steppers in a <form action="/checkout/
    // updategrouporderlines">, so a bare Enter would POST and jump to Shipping
    // (QA/Raminta finding). Swallow the Enter and commit the typed value via a
    // 'change' event instead, so the Order Summary still recomputes.
    document.addEventListener('keydown', event => {
        if (event.key !== 'Enter') {
            return;
        }
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches('[data-amri-qty-input]')) {
            event.preventDefault();
            target.dispatchEvent(new Event('change', { bubbles: true }));
        }
    });
}
